import { createContext, useContext, ReactNode } from 'react'
import { useAgencyConfig } from '../hooks/useAgency'
import type { AgencyAssets } from '../types'

interface AgencyAssetsContextValue {
  assets: AgencyAssets
  agencyName: string
  isLoading: boolean
}

const AgencyAssetsContext = createContext<AgencyAssetsContextValue | undefined>(undefined)

interface AgencyAssetsProviderProps {
  children: ReactNode
}

/**
 * Provider that makes agency assets (logos, images) available throughout the app
 */
export function AgencyAssetsProvider({ children }: AgencyAssetsProviderProps) {
  const { data: agencyConfig, isLoading } = useAgencyConfig()

  const value: AgencyAssetsContextValue = {
    assets: agencyConfig?.assets || {
      logoCompactUrl: undefined,
      logoInlineUrl: undefined,
      loginBackgroundUrl: undefined,
      showInlineLogoOnLogin: true,
    },
    agencyName: agencyConfig?.name || '',
    isLoading,
  }

  return <AgencyAssetsContext.Provider value={value}>{children}</AgencyAssetsContext.Provider>
}

/**
 * Hook to access agency assets
 *
 * Usage:
 * ```tsx
 * const { assets, agencyName } = useAgencyAssets()
 *
 * <img src={assets.logoCompactUrl || '/assets/default-logo.png'} />
 * ```
 */
export function useAgencyAssets() {
  const context = useContext(AgencyAssetsContext)
  if (context === undefined) {
    throw new Error('useAgencyAssets must be used within AgencyAssetsProvider')
  }
  return context
}
